'use client';

import Image from 'next/image';
import { motion } from 'framer-motion';
import { ArrowUpRight, Coins, ExternalLink, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PARENT_SITE } from '@/lib/constants';

interface BullionItem {
  weight: string;
  description: string;
  href: string;
  imageSrc: string;
  badge?: string;
}

// Las imagenes son las oficiales de oro-express.es (SEMPSA), descargadas y
// optimizadas a WebP por scripts/download-bullion-images.mjs.
// Los enlaces apuntan a la ficha real del producto (verificada contra la WC API).
const items: BullionItem[] = [
  {
    weight: '5 g',
    description: 'Entrada al oro físico. Ideal para regalo o ahorro inicial.',
    href: PARENT_SITE.bullionGold5g,
    imageSrc: '/brand/oro-express/bullion-gold-5g.webp',
  },
  {
    weight: '10 g',
    description: 'El formato más vendido para empezar a acumular gramo a gramo.',
    href: PARENT_SITE.bullionGold10g,
    imageSrc: '/brand/oro-express/bullion-gold-10g.webp',
    badge: 'Más vendido',
  },
  {
    weight: '50 g',
    description:
      'Equilibrio entre prima reducida y flexibilidad para vender por partes.',
    href: PARENT_SITE.bullionGold50g,
    imageSrc: '/brand/oro-express/bullion-gold-50g.webp',
  },
  {
    weight: '100 g',
    description:
      'Formato profesional con prima ajustada. Cada lingote en blíster sellado.',
    href: PARENT_SITE.bullionGold100g,
    imageSrc: '/brand/oro-express/bullion-gold-100g.webp',
  },
  {
    weight: '1 kg',
    description:
      'Estándar institucional. Mejor coste por gramo para inversión patrimonial.',
    href: PARENT_SITE.bullionGold1kg,
    imageSrc: '/brand/oro-express/bullion-gold-1kg.webp',
    badge: 'Premium mínima',
  },
];

export function BullionLineup() {
  return (
    <section
      id="lingotes"
      className="container-page py-20 sm:py-24"
      aria-labelledby="bullion-lineup-title"
    >
      <div className="mx-auto max-w-3xl text-center">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="text-xs uppercase tracking-[0.3em] text-gold-300"
        >
          Pasa de la teoría al activo
        </motion.p>
        <motion.h2
          id="bullion-lineup-title"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="heading-display mt-3 text-3xl text-cream sm:text-4xl lg:text-5xl"
        >
          Nuestros{' '}
          <span className="text-gold-gradient">lingotes Oro Express</span>
        </motion.h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-cream/70">
          Oro físico de inversión 999,9 / 1000, en blíster sellado con
          certificado de autenticidad. Compra online o en cualquiera de
          nuestras tiendas.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-cream/60">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-gold-300" />
            Exento de IVA (oro de inversión, art. 140 bis Ley 37/1992)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5 text-gold-300" />
            Recompra garantizada
          </span>
        </div>
      </div>

      <ul className="mx-auto mt-12 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {items.map((item) => (
          <li key={item.weight}>
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex h-full flex-col gap-4 overflow-hidden rounded-2xl border border-gold-500/20 bg-navy-900/40 p-5 backdrop-blur-md transition-all hover:-translate-y-1 hover:border-gold-500/55 hover:shadow-gold-glow"
            >
              {item.badge ? (
                <span className="absolute right-4 top-4 z-10 rounded-full border border-gold-500/35 bg-navy-950/80 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gold-200 backdrop-blur">
                  {item.badge}
                </span>
              ) : null}

              <BullionTile imageSrc={item.imageSrc} weight={item.weight} />

              <div className="space-y-1">
                <p className="font-display text-xl font-semibold text-cream group-hover:text-gold-300">
                  Lingote oro {item.weight}
                </p>
                <p className="text-sm leading-relaxed text-cream/65">
                  {item.description}
                </p>
              </div>

              <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-gold-300">
                Ver en oro-express.es
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
          </li>
        ))}
      </ul>

      <div className="mx-auto mt-12 flex max-w-3xl flex-col items-center gap-3 text-center sm:flex-row sm:justify-center">
        <Button asChild variant="primary" size="lg">
          <a
            href={PARENT_SITE.bullionGold}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver todos los lingotes de oro
            <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
        <Button asChild variant="outline" size="lg">
          <a
            href={PARENT_SITE.shop}
            target="_blank"
            rel="noopener noreferrer"
          >
            Catálogo completo Oro Express
            <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      </div>
      <p className="mx-auto mt-4 max-w-2xl text-center text-xs text-cream/45">
        Precios y disponibilidad gestionados desde la tienda oficial{' '}
        <span className="text-cream/65">oro-express.es</span>. La academia es
        formativa y no participa en la operativa comercial.
      </p>
    </section>
  );
}

/**
 * Tile del lingote con la imagen oficial de oro-express.es (SEMPSA).
 * Se sirve como WebP local para no depender del CDN de la tienda.
 */
function BullionTile({
  imageSrc,
  weight,
}: {
  imageSrc: string;
  weight: string;
}) {
  return (
    <div className="relative aspect-[1.75/1] w-full overflow-hidden rounded-xl border border-gold-500/15 bg-gradient-to-br from-navy-800/60 via-navy-900/70 to-navy-950/80 shadow-inner">
      <Image
        src={imageSrc}
        alt={`Lingote de oro ${weight} SEMPSA — distribuido por Oro Express`}
        fill
        sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 220px"
        className="object-contain p-3 transition-transform duration-500 group-hover:scale-[1.04]"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-50 mix-blend-overlay"
        style={{
          background:
            'radial-gradient(circle at 30% 20%, rgba(255,224,160,0.35) 0%, transparent 55%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.35) 0%, transparent 60%)',
        }}
      />
    </div>
  );
}
